import { TOOL_NAMES } from '@acs/shared';

/**
 * What the assistant has gathered for a booking so far in a Try-step call —
 * shown to the owner as it fills in, and kept when the call ends. Every field
 * is the latest value heard: a caller who changes their mind replaces it.
 */
export interface Collected {
  service?: string;
  /** Business-local date, YYYY-MM-DD. */
  date?: string;
  /** Business-local time, HH:mm. */
  time?: string;
  name?: string;
  /** E.164. */
  phone?: string;
}

/**
 * The caller ID a demo session presents (the gateway's `DEMO_CALLER_NUMBER`).
 * Not a number the owner gave, so never shown as one.
 */
const DEMO_CALLER_NUMBER = '+48000000000';

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const phone = (v: unknown): string | undefined => {
  const p = str(v);
  return p && p !== DEMO_CALLER_NUMBER ? p : undefined;
};

/** `2026-07-17T10:30:00+02:00` → its business-local date and time, as written. */
function splitStart(start: unknown): { date?: string; time?: string } {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(str(start) ?? '');
  return m ? { date: m[1], time: m[2] } : {};
}

/**
 * Fold one completed tool call into what has been collected.
 *
 * `prepare_booking`'s read-back is the authority (the server resolved the
 * service and the slot); its arguments fill in when it refused, and
 * `find_available_slots` names the service before anything is prepared.
 */
export function collectFromTool(
  prev: Collected,
  name: string,
  args: unknown,
  result: unknown,
  serviceName: (id: string) => string | undefined,
): Collected {
  const a = (args ?? {}) as Record<string, unknown>;
  const next = { ...prev };
  const setService = (id: unknown) => {
    const s = str(id);
    const named = s ? serviceName(s) : undefined;
    if (named) next.service = named;
  };

  if (name === TOOL_NAMES.findAvailableSlots) {
    setService(a.service_id);
    return next;
  }
  if (name !== TOOL_NAMES.prepareBooking) return prev;

  setService(a.service_id);
  const { date, time } = splitStart(a.start);
  if (date) next.date = date;
  if (time) next.time = time;
  next.name = str(a.customer_name) ?? next.name;
  next.phone = phone(a.customer_phone) ?? next.phone;

  const r = result as { ok?: boolean; read_back?: Record<string, unknown> } | null;
  const rb = r?.ok ? r.read_back : undefined;
  if (rb) {
    next.service = str(rb.service) ?? next.service;
    next.date = str(rb.date) ?? next.date;
    next.time = str(rb.time) ?? next.time;
    next.name = str(rb.name) ?? next.name;
    next.phone = phone(rb.callback_number) ?? next.phone;
  }
  return next;
}

export const hasCollected = (c: Collected): boolean =>
  Boolean(c.service || c.date || c.time || c.name || c.phone);
